/**
 * 発注ワークフロー型定義(クライアント側)
 *
 * faro-api の `src/domain/types/PurchaseOrder.ts` と整合させた UI 表示用型定義。
 * Date はクライアント側で `Date` オブジェクトとして扱い、API レスポンスの ISO 文字列は
 * `purchaseOrderMapper.ts` で変換する。
 */

// ========== ステータス定数 ==========

/** 発注ステータス */
export const PURCHASE_ORDER_STATUS = {
  ORDERED: 'ORDERED',
  ARRIVED: 'ARRIVED',
  CANCELLED: 'CANCELLED',
} as const;

export type PurchaseOrderStatusValue =
  (typeof PURCHASE_ORDER_STATUS)[keyof typeof PURCHASE_ORDER_STATUS];

/** ステータス表示名(v1.5 で「着荷」→「入庫」に文言変更、設計書 05-frontend-design §3.1) */
export const PURCHASE_ORDER_STATUS_DISPLAY_NAME: Record<PurchaseOrderStatusValue, string> = {
  ORDERED: '入庫待ち',
  ARRIVED: '入庫済',
  CANCELLED: 'キャンセル',
};

/**
 * Ant Design `Tag` の color プロパティ用の値
 *
 * 設計書 05-frontend-design §3.1 の status.color に整合。
 * processing(青、進行中) / success(緑) / default(灰) を使い分ける。
 */
export const PURCHASE_ORDER_STATUS_TAG_COLOR: Record<PurchaseOrderStatusValue, string> = {
  ORDERED: 'processing',
  ARRIVED: 'success',
  CANCELLED: 'default',
};

// ========== キャンセル理由コード ==========

/** 発注キャンセル理由(全種、API 表現用) */
export const PURCHASE_ORDER_CANCEL_REASON = {
  MISTAKE: 'MISTAKE',
  DUPLICATE: 'DUPLICATE',
  DELAY: 'DELAY',
  AUTO_EXPIRED: 'AUTO_EXPIRED',
  OTHER: 'OTHER',
} as const;

export type PurchaseOrderCancelReasonCode =
  (typeof PURCHASE_ORDER_CANCEL_REASON)[keyof typeof PURCHASE_ORDER_CANCEL_REASON];

/** ユーザーが選択可能なキャンセル理由(AUTO_EXPIRED はシステム自動付与専用、UI 選択肢から除外) */
export type UserSelectableCancelReasonCode = Exclude<
  PurchaseOrderCancelReasonCode,
  'AUTO_EXPIRED'
>;

/** ユーザー選択可能なキャンセル理由の選択肢 */
export const USER_SELECTABLE_CANCEL_REASONS: readonly UserSelectableCancelReasonCode[] = [
  'MISTAKE',
  'DUPLICATE',
  'DELAY',
  'OTHER',
];

/** キャンセル理由の表示名 */
export const PURCHASE_ORDER_CANCEL_REASON_DISPLAY_NAME: Record<
  PurchaseOrderCancelReasonCode,
  string
> = {
  MISTAKE: '誤発注',
  DUPLICATE: '重複発注',
  DELAY: '納期遅延',
  AUTO_EXPIRED: '自動キャンセル',
  OTHER: 'その他',
};

// ========== UI デフォルト値 ==========

/**
 * 発注一覧画面のデフォルト値
 *
 * バックエンド(faro-api `PurchaseOrderConstants`)が SoT。本フロント定数は UI 表示用キャッシュ。
 * バックエンドが値を変更した場合、ここも同期する必要がある(後続PRでサーバーレスポンス由来に
 * 切替える想定、設計書 05-frontend-design.md §4.1)。
 */
export const PURCHASE_ORDER_DEFAULTS = {
  PAGE_SIZE: 20,
  FIRST_PAGE: 1,
} as const;

/**
 * 発注ワークフロー業務ルール定数(faro-api PR-2 PurchaseOrderConstants と整合)
 *
 * バックエンドが SoT(Single Source of Truth)であり、本フロント定数はあくまで UI 表示用キャッシュ。
 * バックエンドが値を変更した場合、ここも同期する必要がある。
 * 中長期的にはサーバーレスポンスに reversalDeadlineDate / remainingReversalCount を含めて
 * フロント計算を撤廃する設計が推奨される(DDD 整合、後続PR委譲)。
 */
export const PURCHASE_ORDER_BUSINESS_RULES = {
  /** 着荷確認後に巻き戻し可能な日数(faro-api PURCHASE_ORDER_LIMITS.REVERSAL_DEADLINE_DAYS) */
  REVERSAL_DEADLINE_DAYS: 7,
  /** 1発注における巻き戻し可能回数の上限(faro-api PURCHASE_ORDER_LIMITS.REVERSAL_MAX_COUNT) */
  REVERSAL_MAX_COUNT: 1,
} as const;

// ========== ViewModel(API レスポンス変換後の UI 表示型) ==========

/**
 * 発注 ViewModel
 *
 * 表示用に変換済(Date 型に変換済、isOverdue/isReversible/isCancellable はサーバー暫定値)。
 */
export interface PurchaseOrderViewModel {
  readonly id: number;
  readonly siteId: number;
  readonly siteName: string;
  readonly materialTypeId: number;
  readonly materialTypeCode: string;
  readonly materialTypeName: string;
  readonly orderedQuantity: number;
  readonly expectedArrivalDate: Date;
  readonly orderNote: string | null;
  readonly status: PurchaseOrderStatusValue;

  // 発注時情報
  readonly orderedByUserId: string;
  readonly orderedByUserName: string;
  readonly orderedAt: Date;

  // 着荷確認時情報
  readonly arrivedByUserId: string | null;
  readonly arrivedByUserName: string | null;
  readonly arrivedAt: Date | null;
  readonly arrivalNote: string | null;
  readonly inventoryTransactionId: number | null;

  // 着荷確認永続履歴(D-A 最新保持)
  readonly lastArrivedByUserId: string | null;
  readonly lastArrivedByUserName: string | null;
  readonly lastArrivedAt: Date | null;

  // 巻き戻し時情報
  readonly reversedByUserId: string | null;
  readonly reversedByUserName: string | null;
  readonly reversedAt: Date | null;
  readonly reversalReason: string | null;
  readonly reversalCount: number;

  // キャンセル時情報
  readonly cancelledByUserId: string | null;
  readonly cancelledByUserName: string | null;
  readonly cancelledAt: Date | null;
  readonly cancelReasonCode: PurchaseOrderCancelReasonCode | null;
  readonly cancelReasonDetail: string | null;

  // 派生フラグ(サーバー側で生成された暫定判定、クライアント表示制御用)
  readonly isOverdue: boolean;
  readonly isReversible: boolean;
  readonly isCancellable: boolean;
}

/** ページネーション情報 */
export interface PurchaseOrderPagination {
  readonly currentPage: number;
  readonly totalPages: number;
  readonly totalCount: number;
  readonly hasNextPage: boolean;
  readonly hasPreviousPage: boolean;
}

/** 発注一覧結果 */
export interface PurchaseOrderListResult {
  readonly items: readonly PurchaseOrderViewModel[];
  readonly pagination: PurchaseOrderPagination;
}

/** InventoryTransaction 操作結果サマリ */
export interface PurchaseOrderInventoryTransactionResult {
  readonly transactionId: number;
  readonly quantityBefore: number;
  readonly quantityAfter: number;
}

/** 発注操作後の現在庫サマリ */
export interface PurchaseOrderSiteInventorySummary {
  readonly currentQuantity: number;
  readonly pendingOrderedQuantity: number;
  readonly effectiveQuantity: number;
  readonly isLowStock: boolean;
  readonly isReorderRequired: boolean;
}

/** 着荷確認結果 */
export interface ConfirmArrivalResult {
  readonly purchaseOrder: PurchaseOrderViewModel;
  readonly inventoryTransaction: PurchaseOrderInventoryTransactionResult;
  readonly currentInventory: PurchaseOrderSiteInventorySummary;
}

/** 着荷取消(巻き戻し)結果 */
export interface ReverseArrivalResult {
  readonly purchaseOrder: PurchaseOrderViewModel;
  readonly inventoryTransaction: PurchaseOrderInventoryTransactionResult;
  readonly currentInventory: PurchaseOrderSiteInventorySummary;
}

// ========== リクエスト型 ==========

/** 発注登録リクエスト */
export interface CreatePurchaseOrderRequest {
  readonly siteId: number;
  readonly materialTypeId: number;
  readonly orderedQuantity: number;
  /** ISO 日付文字列(YYYY-MM-DD、JST 基準) */
  readonly expectedArrivalDate: string;
  readonly orderNote: string | null;
}

/** 一覧取得クエリ */
export interface PurchaseOrderListQuery {
  readonly siteId?: number;
  readonly status?: PurchaseOrderStatusValue;
  readonly materialTypeId?: number;
  /** ISO 日付文字列(YYYY-MM-DD) */
  readonly expectedArrivalDateFrom?: string;
  /** ISO 日付文字列(YYYY-MM-DD) */
  readonly expectedArrivalDateTo?: string;
  readonly includeArchived?: boolean;
  readonly page?: number;
  readonly limit?: number;
}

/**
 * フィルタ適用入力(page を除外)
 *
 * フィルタ変更時はフック側で page を 1 ページ目に強制リセットするため、
 * 呼出側は page を渡せない契約とする。
 */
export type ApplyFilterInput = Omit<PurchaseOrderListQuery, 'page'>;

/** 材料種別の選択肢(InventoryService の material types から構築) */
export interface MaterialTypeOption {
  readonly value: number;
  readonly label: string;
}

/** 現場の選択肢(発注一覧 items または user.site から構築) */
export interface SiteOption {
  readonly value: number;
  readonly label: string;
}

/**
 * 現場フィルタの表示モード(判別共用体)
 *
 * 設計書 05-frontend-design.md §5.1「現場フィルタ(MCM/DEV: 選択可、SITE_*: 固定表示)」を
 * 排他的な状態として表現。UI コンポーネント側は `kind` で switch 網羅性チェックを行い、
 * `siteOptions: ... | null` + `fixedSiteLabel: ... | null` の 2-prop 多義設計を解消する。
 */
export type SiteFilterMode =
  | { readonly kind: 'selectable'; readonly options: readonly SiteOption[] }
  | { readonly kind: 'fixed'; readonly label: string }
  | { readonly kind: 'hidden' };

/**
 * 発注画面で必要な現場関連の派生情報
 *
 * 登録モーダル用 / フィルタ用の選択肢・固定値を 1 構造に集約し、
 * `siteOptionsBuilder.buildSiteSelectionContext` で一括構築する。
 */
export interface SiteSelectionContext {
  /** 登録モーダル用の現場選択肢(MCM/DEV: 全現場、SITE_*: 自サイトのみ) */
  readonly registrationOptions: readonly SiteOption[];
  /** 登録モーダル用に固定する siteId(管理者は null) */
  readonly fixedRegistrationSiteId: number | null;
  /** 一覧フィルタの現場表示モード */
  readonly filterMode: SiteFilterMode;
}

/** 着荷確認リクエスト */
export interface ConfirmArrivalRequest {
  readonly arrivalNote: string | null;
}

/** 着荷取消(巻き戻し)リクエスト */
export interface ReverseArrivalRequest {
  readonly reversalReason: string | null;
}

/** 発注キャンセルリクエスト */
export interface CancelPurchaseOrderRequest {
  readonly cancelReasonCode: UserSelectableCancelReasonCode;
  /** OTHER 指定時は必須、それ以外は null/任意 */
  readonly cancelReasonDetail: string | null;
}
